import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Download, Loader2, ArrowLeft, Play, Eye, Wand2, Hash, UserPlus, UserMinus, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { useEditor } from '../context/EditorContext';
import { toast } from '../hooks/use-toast';
import Comments from './Comments';

const REACTIONS = ['🔥', '❤️', '😂', '😍', '🎉', '👏', '🤯', '😱'];

const VideoView = () => {
  const { id } = useParams();
  const { lang } = useLang();
  const { user, login } = useAuth();
  const { openEditorWithVideo } = useEditor();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [author, setAuthor] = useState(null);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reactions, setReactions] = useState({});
  const [flying, setFlying] = useState([]);
  const viewLoggedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/videos/${id}`);
        setVideo(res.data);
        if (!viewLoggedRef.current) {
          viewLoggedRef.current = true;
          axios.post(`${API}/videos/${id}/view`).then(r => setVideo(v => v ? { ...v, view_count: r.data.view_count } : v)).catch(() => {});
        }
        const rRes = await axios.get(`${API}/videos/${id}/reactions`);
        setReactions(rRes.data.reactions || {});
        // Fetch author profile
        if (res.data.user_id) {
          try {
            const [uRes, fRes] = await Promise.all([
              axios.get(`${API}/users/${res.data.user_id}`),
              axios.get(`${API}/follow/status/${res.data.user_id}`, { withCredentials: true })
            ]);
            setAuthor({ ...uRes.data.user, followers: uRes.data.followers });
            setFollowing(fRes.data.following);
          } catch (_) {}
        }
      } catch (e) { setError(e.response?.data?.detail || 'Video not found'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const toggleFollow = async () => {
    if (!user) { login(); return; }
    if (!author) return;
    try {
      if (following) {
        await axios.delete(`${API}/follow/${author.user_id}`, { withCredentials: true });
        setFollowing(false);
        setAuthor(a => a ? { ...a, followers: Math.max(0, (a.followers || 0) - 1) } : a);
      } else {
        await axios.post(`${API}/follow/${author.user_id}`, {}, { withCredentials: true });
        setFollowing(true);
        setAuthor(a => a ? { ...a, followers: (a.followers || 0) + 1 } : a);
        toast({ title: lang === 'sv' ? 'Följer nu!' : 'أنت الآن تتابع!' });
      }
    } catch (_) {}
  };

  const react = async (emoji) => {
    // Optimistic update
    setReactions(prev => ({ ...prev, [emoji]: (prev[emoji] || 0) + 1 }));
    // Flying emoji animation
    const key = `${emoji}${Date.now()}${Math.random()}`;
    setFlying(prev => [...prev, { key, emoji, left: Math.random() * 60 + 20 }]);
    setTimeout(() => setFlying(prev => prev.filter(f => f.key !== key)), 1400);
    try { await axios.post(`${API}/videos/${id}/react`, { emoji }); } catch (_) {}
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  if (error || !video) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-950 text-white gap-4">
      <p className="text-neutral-400">{lang === 'sv' ? 'Videon kunde inte hittas' : 'الفيديو غير موجود'}</p>
      <a href="/" className="text-cyan-400 font-semibold text-sm">{lang === 'sv' ? 'Tillbaka till startsidan' : 'العودة إلى الرئيسية'}</a>
    </div>
  );

  const videoSrc = `${API}/files/${video.storage_path}`;
  const thumbSrc = video.thumb_path ? `${API}/files/${video.thumb_path}` : null;
  const totalReactions = Object.values(reactions).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <div className="flex items-center justify-between mb-6">
          <a href="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-[13px] font-semibold"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {lang === 'sv' ? 'Tillbaka' : 'رجوع'}</a>
          <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div><span className="font-black text-white text-[15px]">CapCute</span></div>
        </div>

        <div className="relative rounded-3xl overflow-hidden bg-black shadow-2xl border border-white/10 aspect-video">
          <video src={videoSrc} poster={thumbSrc || undefined} controls playsInline className="w-full h-full object-contain" />
          {/* Flying reactions overlay */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {flying.map(f => (
              <div key={f.key} className="absolute bottom-4 text-4xl animate-fly" style={{ left: `${f.left}%` }}>{f.emoji}</div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">{video.title}</h1>
            <p className="text-[13px] text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>{new Date(video.created_at).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {video.view_count || 0} {lang === 'sv' ? 'visningar' : 'مشاهدة'}</span>
              {video.duration ? (<><span>·</span><span>{Math.round(video.duration)}s</span></>) : null}
            </p>
            {video.tags && video.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {video.tags.map(t => (
                  <a key={t} href={`/t/${encodeURIComponent(t)}`} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-cyan-300 hover:text-cyan-200 text-[11.5px] font-semibold transition-colors">
                    <Hash className="w-3 h-3" /> {t}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => {
              openEditorWithVideo && openEditorWithVideo({
                videoUrl: videoSrc,
                videoName: video.title,
                settings: video.settings || null
              });
              navigate('/');
            }} className="h-11 px-5 rounded-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:opacity-90 text-white font-bold shadow-lg">
              <Wand2 className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {lang === 'sv' ? 'Använd som mall' : 'استخدم كقالب'}
            </Button>
            <Button asChild className="h-11 px-5 rounded-full bg-white text-black hover:bg-neutral-200 font-bold">
              <a href={videoSrc} download><Download className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />{lang === 'sv' ? 'Ladda ner' : 'تنزيل'}</a>
            </Button>
            <Button data-testid="share-video-btn" onClick={async () => {
              try { await navigator.clipboard.writeText(window.location.href); toast({ title: lang === 'sv' ? 'Länk kopierad!' : 'تم نسخ الرابط!', description: lang === 'sv' ? 'Klistra in var du vill dela videon' : 'الصقه أينما تريد المشاركة' }); }
              catch (_) { toast({ title: lang === 'sv' ? 'Kunde inte kopiera' : 'فشل النسخ' }); }
            }} className="h-11 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold border border-white/20"><Share2 className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />{lang === 'sv' ? 'Dela' : 'مشاركة'}</Button>
            <Button asChild variant="outline" className="h-11 px-5 rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white font-bold">
              <a href="/"><Play className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2 fill-white" />{lang === 'sv' ? 'Startsidan' : 'الرئيسية'}</a>
            </Button>
          </div>
        </div>

        {/* Author card */}
        {author && (
          <div className="mt-5 p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
            <a href={`/u/${author.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
              {author.picture ? <img src={author.picture} alt={author.name} className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center text-white font-bold">{author.name?.[0] || 'U'}</div>}
              <div className="min-w-0">
                <div className="text-[15px] font-bold truncate">{author.name}</div>
                <div className="text-[11.5px] text-neutral-500">{author.followers || 0} {lang === 'sv' ? 'följare' : 'متابع'}</div>
              </div>
            </a>
            {user?.user_id !== author.user_id && (
              <Button onClick={toggleFollow} className={`h-10 px-4 rounded-full font-bold text-[12.5px] flex-shrink-0 ${following ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-white text-black hover:bg-neutral-200'}`}>
                {following ? <><UserMinus className="w-3.5 h-3.5 mr-1.5 rtl:mr-0 rtl:ml-1.5" /> {lang === 'sv' ? 'Följer' : 'يتابع'}</> : <><UserPlus className="w-3.5 h-3.5 mr-1.5 rtl:mr-0 rtl:ml-1.5" /> {lang === 'sv' ? 'Följ' : 'تابع'}</>}
              </Button>
            )}
          </div>
        )}

        {/* Reactions bar */}
        <div className="mt-6 p-4 md:p-5 rounded-3xl bg-white/5 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11.5px] uppercase tracking-wider font-bold text-neutral-400">{lang === 'sv' ? 'Reaktioner' : 'التفاعلات'}</div>
            <div className="text-[12px] text-neutral-500 font-semibold">{totalReactions} {lang === 'sv' ? 'totalt' : 'الإجمالي'}</div>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
            {REACTIONS.map(em => (
              <button key={em} onClick={() => react(em)} className="group flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 hover:bg-white/15 hover:scale-110 transition-all">
                <span className="text-3xl group-active:scale-125 transition-transform">{em}</span>
                <span className="text-[11px] font-bold tabular-nums text-neutral-300">{reactions[em] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Comments */}
        <Comments videoId={id} />
      </div>
      <style>{`
        @keyframes fly {
          0% { transform: translateY(0) scale(0.6); opacity: 0; }
          15% { transform: translateY(-40px) scale(1.1); opacity: 1; }
          100% { transform: translateY(-320px) scale(0.9); opacity: 0; }
        }
        .animate-fly { animation: fly 1.4s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default VideoView;
